import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { onTourImageError } from "@/lib/imageFallback";
import { listFeaturedEvents, listEvents, formatEventDate, type EventItem } from "@/lib/events";

/**
 * Homepage "Próximos eventos" banner — admin-curated special happenings in the zone.
 * Renders a large featured event + a few upcoming ones. Hides itself entirely when
 * there are no upcoming events, so the homepage never shows an empty section.
 */
export default function UpcomingEventsSection() {
  const { t } = useTranslation("events");
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let list = await listFeaturedEvents(4);
        if (list.length === 0) list = await listEvents(undefined, 4);
        if (!cancelled) setEvents(list);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || events.length === 0) return null;

  const [hero, ...rest] = events;

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-charcoal">{t("events.upcomingTitle")}</h2>
            <p className="text-gray-500 text-sm mt-1">{t("events.upcomingSubtitle")}</p>
          </div>
          <Link to="/eventos" className="text-sm text-ocean font-medium hover:underline whitespace-nowrap">
            {t("events.seeAll")}
          </Link>
        </div>

        {/* Large featured event */}
        <Link
          to={`/eventos/${hero.slug}`}
          className="group relative block rounded-2xl overflow-hidden h-72 md:h-96 mb-6"
        >
          <img
            src={hero.imageUrl ?? "/brand/hero/i4-1200.jpg"}
            alt={hero.title}
            onError={onTourImageError}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 bg-coral text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
              <i className="ri-calendar-event-line" />
              {formatEventDate(hero)}
            </span>
            <h3 className="font-display text-2xl md:text-4xl font-bold text-white max-w-2xl leading-tight">
              {hero.title}
            </h3>
            {hero.shortDescription && (
              <p className="text-white/85 text-sm md:text-base mt-2 max-w-xl">{hero.shortDescription}</p>
            )}
            <div className="flex items-center gap-4 mt-4 text-white/90 text-sm">
              {(hero.location || hero.city) && (
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line" /> {[hero.location, hero.city].filter(Boolean).join(", ")}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 font-semibold group-hover:gap-2.5 transition-all">
                {t("events.viewEvent")} <i className="ri-arrow-right-line" />
              </span>
            </div>
          </div>
        </Link>

        {/* Other upcoming events */}
        {rest.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {rest.map((ev) => (
              <Link
                key={ev.id}
                to={`/eventos/${ev.slug}`}
                className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={ev.imageUrl ?? "/brand/hero/i1-1200.jpg"}
                    alt={ev.title}
                    onError={onTourImageError}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs text-coral font-semibold mb-1">{formatEventDate(ev)}</p>
                  <h3 className="font-semibold text-charcoal text-sm leading-snug line-clamp-2">{ev.title}</h3>
                  {(ev.location || ev.city) && (
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <i className="ri-map-pin-line" /> {[ev.location, ev.city].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
